import cx from 'classnames'
import Styles from './ChatMessages.module.scss'
import { Pill } from 'components/Pill/Pill'
import { useButton } from '@react-aria/button'
import { useRef } from 'react'
import { Image03 } from 'blaze-ui/icons/24'
import { Caption, ColorSwatch2, Menu2 } from 'blaze-ui/icons'
import { useHover } from '@react-aria/interactions'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Button } from 'blaze-ui/Button'
import { Icon } from 'almanac-ui/icons/Types'

interface EmptyStateProps {
  setValue: (value: string) => void
}

interface Suggestion {
  name: string
  prompt: string
  backgroundColor?: string
  textColor?: string
  icon: React.ReactNode | Icon
}

export const PROMPT_SUGGESTIONS: Record<string, string> = {
  image: 'Change the image in the design',
  colors: 'Change the colors in the design',
  text: 'Change the text in the design',
  caption: 'Change the caption for the post',
}

export const SUGGESTIONS: Suggestion[] = [
  {
    name: 'Change image',
    prompt: PROMPT_SUGGESTIONS.image,
    backgroundColor: 'rgba(182, 75, 248, 0.10)',
    textColor: 'var(--magenta-70)',
    icon: <Image03 size={16} color="var(--magenta-50)" />,
  },
  {
    name: 'Change colors',
    prompt: PROMPT_SUGGESTIONS.colors,
    backgroundColor: 'rgba(255, 132, 43, 0.10)',
    textColor: '#B94E00',
    icon: <ColorSwatch2 size={16} color="var(--orange-50)" />,
  },
  {
    name: 'Change text',
    prompt: PROMPT_SUGGESTIONS.text,
    backgroundColor: 'rgba(79, 98, 248, 0.10)',
    textColor: '#3B4BCF',
    icon: <Menu2 size={16} color="var(--purple-50)" />,
  },
  {
    name: 'Change caption',
    prompt: PROMPT_SUGGESTIONS.caption,
    backgroundColor: 'rgba(4, 175, 0, 0.10)',
    textColor: 'var(--green-70)',
    icon: <Caption size={16} color="var(--green-50)" />,
  },
]

export const SUGGESTIONS_V2: Suggestion[] = [
  {
    name: 'Change image',
    prompt: PROMPT_SUGGESTIONS.image,
    icon: Image03,
  },
  {
    name: 'Change colors',
    prompt: PROMPT_SUGGESTIONS.colors,
    icon: ColorSwatch2,
  },
  {
    name: 'Change text',
    prompt: PROMPT_SUGGESTIONS.text,
    backgroundColor: 'var(--dark-3)',
    icon: Menu2,
  },
  {
    name: 'Change caption',
    prompt: PROMPT_SUGGESTIONS.caption,
    icon: Caption,
  },
]

const SuggestionPill = ({ suggestion, setValue }: { suggestion: Suggestion; setValue: (value: string) => void }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const ref = useRef<HTMLDivElement>(null)
  const { buttonProps } = useButton(
    {
      onPress: () => setValue(suggestion.prompt),
      elementType: 'div',
    },
    ref,
  )
  const { hoverProps, isHovered } = useHover({})

  if (isSystemRedesignEnabled) {
    return (
      <div ref={ref}>
        <Button
          size="md"
          variant="secondary"
          className={Styles.suggestionButton}
          frontIcon={suggestion.icon as Icon}
          onPress={() => setValue(suggestion.prompt)}
        >
          {suggestion.name}
        </Button>
      </div>
    )
  }

  return (
    <div ref={ref} {...buttonProps} {...hoverProps}>
      <Pill
        size="lg"
        className={cx(Styles.suggestionPill, { [Styles.hovered]: isHovered })}
        style={{ backgroundColor: suggestion.backgroundColor, color: suggestion.textColor }}
      >
        {suggestion.icon as React.ReactNode}
        {suggestion.name}
      </Pill>
    </div>
  )
}

export const Suggestions = ({ setValue }: EmptyStateProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const suggestions = isSystemRedesignEnabled ? SUGGESTIONS_V2 : SUGGESTIONS

  return (
    <div className={Styles.suggestions}>
      {suggestions.map((suggestion) => (
        <SuggestionPill key={suggestion.name} suggestion={suggestion} setValue={setValue} />
      ))}
    </div>
  )
}
